/**
 * User Relationship Utilities
 * 
 * Helper functions to determine user's relationship to programs and cohorts
 */

import { CohortSummary } from '../api/programs';

export type UserRole = 'owner' | 'admin' | 'mentor' | 'participant';

export interface UserRelationship {
  role: UserRole;
}

/**
 * Get user's relationship to a specific cohort
 * 
 * @param cohortId - The cohort ID to check (unused when booleans are pre-computed, kept for API consistency)
 * @param isAdmin - Whether the user is a program admin (applies to all cohorts)
 * @param isMentor - Whether the user is a mentor for this cohort
 * @param isParticipant - Whether the user is a participant in this cohort
 * @returns User relationship object or null if no relationship
 */
export function getUserCohortRelationship(
  _cohortId: string,
  isAdmin: boolean,
  isMentor: boolean,
  isParticipant: boolean,
  isOwner: boolean = false,
): UserRelationship | null {
  // Owner is highest priority
  if (isOwner) {
    return { role: 'owner' };
  }

  if (isAdmin) {
    return { role: 'admin' };
  }

  if (isMentor) {
    return { role: 'mentor' };
  }

  if (isParticipant) {
    return { role: 'participant' };
  }

  return null;
}

/**
 * Get user's relationship to a program based on a specific cohort summary
 * Used on the TrainingPrograms page where cohort summaries include user flags
 * 
 * @param cohort - The cohort summary (can be null if no relevant cohort)
 * @param isAdmin - Whether the user is a program admin
 * @param isOwner - Whether the user is the program owner
 * @returns User relationship object or null if no relationship
 */
export function getUserProgramRelationship(
  cohort: CohortSummary | null,
  isAdmin: boolean,
  isOwner: boolean = false,
): UserRelationship | null {
  // Owner is highest priority
  if (isOwner) {
    return { role: 'owner' };
  }

  if (!cohort) {
    return isAdmin ? { role: 'admin' } : null;
  }

  if (isAdmin) {
    return { role: 'admin' };
  }

  if (cohort.userIsMentor) {
    return { role: 'mentor' };
  }

  if (cohort.userIsParticipant) {
    return { role: 'participant' };
  }

  return null;
}
